<script setup lang="ts">
import { ProposedDelivery } from '@nuts/auto-delivery-sdk';
import { from, multiply } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { useElementVisibility, useMounted } from '@vueuse/core';
import { useHead } from '@vueuse/head';
import { storeToRefs } from 'pinia';
import { computed, onMounted, reactive, ref, toRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { AlgoliaVariant } from '@/api/algolia';
import AddToAutoDelivery from '@/components/auto-delivery/AddToAutoDelivery.vue';
import AutoDeliveryModal, {
  AutoDeliveryModalSteps,
} from '@/components/auto-delivery/modals/AutoDeliveryModal.vue';
import UpcomingSubscriptionNotice from '@/components/auto-delivery/UpcomingSubscriptionNotice.vue';
import AddToCartQuantitySelect from '@/components/base/add-to-cart/AddToCartQuantitySelect.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import AppliedDiscounts from '@/components/pdp/AppliedDiscounts.vue';
import AddOnModal from '@/components/pdp/buy-block/add-on/AddOnModal.vue';
import AddOnSection from '@/components/pdp/buy-block/add-on/AddOnSection.vue';
import AutoDeliveryBenefits from '@/components/pdp/buy-block/AutoDeliveryBenefits.vue';
import BulkDiscounts from '@/components/pdp/buy-block/BulkDiscounts.vue';
import DeliveryEstimate from '@/components/pdp/buy-block/DeliveryEstimate.vue';
import DeliveryOptions from '@/components/pdp/buy-block/DeliveryOptions.vue';
import GiftCheckbox from '@/components/pdp/buy-block/GiftCheckbox.vue';
import UpsellingProducts from '@/components/pdp/buy-block/UpsellingProducts.vue';
import FavoriteButton from '@/components/pdp/FavoriteButton.vue';
import FreeShippingNotice from '@/components/pdp/impulse-upsell/FreeShippingNotice.vue';
import VariantSizeSelector from '@/components/ppa/VariantSizeSelector.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useLoginModal } from '@/composables/useLoginModal';
import { useProductDetail } from '@/composables/useProductDetail';
import money from '@/filters/money';
import {
  DEFAULT_DELIVERY_INTERVAL_DAYS,
  DEFAULT_DELIVERY_INTERVAL_WEEKS,
} from '@/lib/autoDelivery';
import { getAutoDeliveryInterval } from '@/lib/cart/lineItem';
import { getListPrice, getMoneyValue } from '@/lib/product/prices';
import { useAutoDelivery } from '@/stores/autoDelivery';
import { useCustomer } from '@/stores/customer';
import { gtag, ListMetadata } from '@/utils/analytics';
import { PageSection } from '@/utils/analytics/PageSection';
import { NutsAddToCartItem, sendProductAddedEvent } from '@/utils/analytics/productAddedEvent';
import { sendProductSubscriptionCreatedEvent } from '@/utils/analytics/productSubscriptionCreatedEvent';
import { AddToCartPayload, getCartId } from '@/utils/cart';
import { computeSavings, Money } from '@/utils/money';
import {
  getPrice,
  getUnitName,
  MAX_QUANTITY,
  NutsProduct,
  PredictedShipping,
  priceForQuantity,
  PurchaseOptions,
} from '@/utils/product';
import { ProductCardData } from '@/utils/productCard';

const ADD_ON_MODAL_TITLE = 'Select Something Extra';

const props = defineProps<{
  availableAddOns?: AlgoliaVariant[];
  isFavorited: boolean;
  listMetadata?: ListMetadata;
  predictedShipping?: PredictedShipping;
  product: NutsProduct;
  showMiniPdp?: boolean;
}>();

defineEmits<{
  'show-full-pdp': [buttonName: string];
}>();

const selections = defineModel<PurchaseOptions>({ required: true });

const footer = ref<HTMLElement>();
const addToCartButton = ref<HTMLElement>();

const isAddOnModalOpen = ref(false);
const selectedAddOns = ref<{ addOn: ProductCardData; quantity: number }[]>([]);

const addOnsTotalPrice = computed(() =>
  Money.sumBy(selectedAddOns.value, (s) =>
    multiply(getMoneyValue(s.addOn.listPrice, s.quantity), s.quantity),
  ),
);

const openAddOnModal = () => {
  isAddOnModalOpen.value = true;
  gtag('event', 'open_add_ons_modal');
};

const {
  allBackordered,
  calculateCostSavings,
  isGiftCertificate,
  getRelativeBasePrice,
  variantsWithTiers,
} = useProductDetail(toRef(props, 'product'));

const store = useStore();
const router = useRouter();

const autoDeliveryStore = useAutoDelivery();
const { activeSubscriptionsBySKU, autoDeliveryChannel, nextDelivery } =
  storeToRefs(autoDeliveryStore);
const { addToCart, cart, containsAutoDelivery, lineItems, maxQuantity } = useCart(store);
const { customer } = storeToRefs(useCustomer());
const isAddToCartVisible = useElementVisibility(addToCartButton);
const isFooterVisible = useElementVisibility(footer);
const { flags } = useFeatureFlags();
const loginModal = useLoginModal();
const isMounted = useMounted();

const autoDeliverySignUpModal = reactive({
  interval: 6,
  isOpen: false,
  step: 'sign-up' as AutoDeliveryModalSteps,
});

const openAutoDeliveryModal = (step: AutoDeliveryModalSteps) => {
  autoDeliverySignUpModal.interval = DEFAULT_DELIVERY_INTERVAL_WEEKS;
  autoDeliverySignUpModal.step = step;
  autoDeliverySignUpModal.isOpen = true;
};

const closeAutoDeliveryModal = () => {
  autoDeliverySignUpModal.isOpen = false;
};

const selectedVariant = computed(() => {
  const { variants } = props.product;

  if (!selections.value.sku) return variants[0];

  return (
    variants.find((v) =>
      isGiftCertificate.value
        ? v.id === selections.value.variation
        : v.sku === selections.value.sku,
    ) ?? variants[0]
  );
});

const discounts = computed(() => calculateCostSavings(selectedVariant.value, selections.value));
const finalPrice = computed(() =>
  getPrice(selectedVariant.value, selections.value.autoDelivery?.key),
);
const savings = computed(() => {
  const base = finalPrice.value.discounted
    ? getPrice(selectedVariant.value).value
    : getRelativeBasePrice(selectedVariant.value).value;
  const final = priceForQuantity(selectedVariant.value, selections.value);

  const { percent, value } = computeSavings(base, final);
  return { percent, value: multiply(value, selections.value.quantity) };
});

const productDiscountNotice = computed(() => {
  if (!selectedVariant.value.prices) return undefined;

  const listPrice = getListPrice(selectedVariant.value.prices);
  if (!listPrice.discounted) return undefined;

  let header = 'Enjoy the sale discount!';
  let message = 'Great news! This product is currently on sale';

  if (!selectedVariant.value.wholesale) {
    header = 'Enjoy both sale discount and the subscription discount!';

    message += ` and as a subscriber, you automatically enjoy this discount + the regular subscriber discount while the sale lasts.`;
  }

  return { header, message };
});

const isEligibleForAutoDelivery = computed(() => selectedVariant.value.autoDeliveryEligible);
const hasBulkDiscount = computed(() => variantsWithTiers.value.length > 0);

const autoDeliveryInterval = computed(() => {
  if (!isEligibleForAutoDelivery.value || !selections.value.autoDelivery) return undefined;
  const lineItem = lineItems.value.find((e) => !!e.custom?.fields?.autoDeliveryInterval);
  const existingInterval = lineItem && getAutoDeliveryInterval(lineItem, 'weeks');
  return existingInterval ?? autoDeliverySignUpModal.interval;
});

const canDisplayVariantSelector = computed(
  () => isGiftCertificate.value || props.product.variants.some((v) => v.unitName),
);

const totalPrice = computed(() => {
  let selectionsQuantity = Math.max(Number(selections.value.quantity), 1);
  if (Number.isNaN(selectionsQuantity)) selectionsQuantity = 1;

  const selectionsPrice = from(priceForQuantity(selectedVariant.value, selections.value));

  const selectionsTotalPrice = multiply(selectionsPrice, selectionsQuantity);

  if (selectedAddOns.value.length) {
    return Money.add(selectionsTotalPrice, addOnsTotalPrice.value);
  }

  return selectionsTotalPrice;
});

const addToCartPayload = computed<AddToCartPayload>(() => {
  const { autoDelivery, markedAsGift, quantity, sku, variation } = selections.value;
  const isAutoDelivery = Boolean(autoDelivery && isEligibleForAutoDelivery.value);

  return {
    cart_sku: {
      auto_delivery_interval_weeks: autoDeliveryInterval.value,
      auto_delivery_offer_location: isAutoDelivery ? 'PDP' : undefined,
      auto_delivery_offer_type: isAutoDelivery ? 'Standard' : undefined,
      distribution_channel_key: selectedVariant.value.wholesale ? null : autoDelivery?.key ?? null,
      list_metadata: JSON.stringify(props.listMetadata),
      marked_as_gift: markedAsGift,
      quantity,
      sku_external_id: sku,
      variations: isGiftCertificate.value ? [variation] : undefined,
    },
  };
});

const getNutsAddToCartItem = (
  pageSection: PageSection<'Buy Block' | 'Mini PDP'> = 'Buy Block',
): NutsAddToCartItem => {
  const discount = finalPrice.value.discounted
    ? computeSavings(finalPrice.value.value, finalPrice.value.discounted.value).value
    : undefined;
  return {
    autodeliveryInterval: autoDeliveryInterval.value
      ? autoDeliveryInterval.value * 7
      : autoDeliveryInterval.value,
    cartId: getCartId(cart.value),
    cost: selectedVariant.value.pieceCost,
    coupon: finalPrice.value.discounted?.discount.description ?? undefined,
    discount,
    indexName: props.listMetadata?.indexName,
    itemListName: props.listMetadata?.list,
    markedAsGift: selections.value.markedAsGift,
    name: props.product.name,
    pageSection,
    position: props.listMetadata?.position,
    price: priceForQuantity(selectedVariant.value, selections.value),
    priceBeforeDiscount: getListPrice(selectedVariant.value.prices!).value,
    productId: props.product.key,
    quantity: selections.value.quantity,
    reportingCategory: selectedVariant.value.reportingCategory,
    searchQueryID: props.listMetadata?.searchQueryID,
    sku: selectedVariant.value.sku,
    variant: getUnitName(selectedVariant.value.variantName) ?? undefined,
    variantLabel: selectedVariant.value.variantLabel,
    weight: selectedVariant.value.weight,
  };
};

const sendProductAddedEventForAddOns = () => {
  selectedAddOns.value.forEach(({ addOn, quantity }) => {
    const discount = addOn.listPrice.discounted
      ? computeSavings(addOn.listPrice.value, addOn.listPrice.discounted.value).value
      : undefined;
    sendProductAddedEvent({
      cartId: getCartId(cart.value),
      cost: from(Number(addOn.cost)),
      discount,
      listSourceAttribution: {
        listAlgoliaSearchId: addOn.searchQueryId,
        listSource: 'Algolia',
        listTitle: ADD_ON_MODAL_TITLE,
      },
      name: addOn.name,
      pageSection: 'Buy Block',
      price: getMoneyValue(addOn.listPrice, quantity),
      priceBeforeDiscount: getListPrice(addOn.prices!).value,
      productId: addOn.productKey,
      quantity,
      reportingCategory: addOn.reportingCategory,
      sku: addOn.sku,
      variant: addOn.unitName,
      variantLabel: addOn.variantLabel,
      weight: addOn.weight !== undefined ? Number(addOn.weight) : undefined,
    });
  });
};

const handleProductAdded = useCallback(
  async (pageSection?: PageSection<'Buy Block' | 'Mini PDP'>) => {
    sendProductAddedEvent(getNutsAddToCartItem(pageSection));

    const addToCartPromises = [
      addToCart(addToCartPayload.value, {
        router,
        postAddToCartCallback: () => {},
      }),
    ];

    if (selectedAddOns.value.length) {
      sendProductAddedEventForAddOns();
      addToCartPromises.push(
        ...selectedAddOns.value.map(({ addOn, quantity }) =>
          addToCart(
            {
              cart_sku: {
                sku_external_id: addOn.sku,
                quantity,
              },
            },
            {
              isComplementaryItem: true,
              postAddToCartCallback: () => {},
            },
          ),
        ),
      );
    }

    await Promise.all(addToCartPromises);
  },
);

const handleStartAutoDelivery = useCallback(async () => {
  const hadAutoDelivery = !!selections.value.autoDelivery;
  selections.value.autoDelivery = autoDeliveryChannel.value;

  await handleProductAdded.execute();
  if (!hadAutoDelivery) selections.value.autoDelivery = undefined;
  closeAutoDeliveryModal();
});

const handleAddToAutoDelivery = useCallback(async () => {
  if (containsAutoDelivery.value) {
    await handleProductAdded.execute();
  } else {
    openAutoDeliveryModal('sign-up');
  }
});

onMounted(() => {
  footer.value = document.querySelector('footer') ?? undefined;
});

useHead({
  bodyAttrs: {
    class: computed(() => (isAddToCartVisible.value ? [] : ['offset-bottom-anchored-elements'])),
  },
});

const onAddToNextDelivery = useCallback(async (delivery: ProposedDelivery, oneTime?: boolean) => {
  const interval = oneTime
    ? undefined
    : Math.min(...delivery.subscriptions.map((s) => s.interval || DEFAULT_DELIVERY_INTERVAL_DAYS));

  await autoDeliveryStore.createSubscription({
    active: true,
    interval,
    nextShipOn: delivery.shipOn,
    offerLocation: 'PDP',
    offerType: oneTime ? 'IU Upsell' : 'IU Recurring',
    quantity: 1,
    shippingAddressId: delivery.shippingAddressId,
    sku: selections.value.sku,
  });

  await Promise.all([
    autoDeliveryStore.getOrders({
      preloadPaymentMethod: false,
      preloadShippingAddresses: false,
    }),
    autoDeliveryStore.getActiveSubscriptions({
      preloadPaymentMethod: false,
      preloadShippingAddresses: false,
    }),
  ]);

  const createdSubscription = activeSubscriptionsBySKU.value.get(selectedVariant.value.sku)?.[0];

  if (createdSubscription) {
    sendProductSubscriptionCreatedEvent({
      pageSection: 'Auto-Delivery Sign-Up Modal',
      productKey: props.product.key,
      subscription: createdSubscription,
    });
  }
});

const handleAutoDeliveryUpsell = async (delivery?: ProposedDelivery, oneTime?: boolean) => {
  if (delivery) {
    await onAddToNextDelivery.execute(delivery, oneTime);
    closeAutoDeliveryModal();
    autoDeliveryStore.openAddedToAutoDelivery(delivery, selections.value.sku);
  } else {
    loginModal.handleOpen();
  }
};

watch(
  () => props.product.offerGiftAddOns,
  (offerGiftAddOns) => {
    if (!offerGiftAddOns) selectedAddOns.value = [];
  },
);
</script>

<template>
  <div v-if="showMiniPdp">
    <UnstyledButton
      class="hidden mt-6 lg:visible lg:block"
      @click="$emit('show-full-pdp', 'See all sizes')"
    >
      <SmallBodyText class="text-cyan-700 underline">See all sizes</SmallBodyText>
    </UnstyledButton>
    <div class="flex gap-2 mt-4 lg:mt-6 lg:flex-row-reverse">
      <ThemedButton fullWidth theme="white" @click="$emit('show-full-pdp', 'View full details')">
        View Full Details
      </ThemedButton>
      <ThemedButton fullWidth @click="handleProductAdded.execute('Mini PDP')">
        Add to Cart <span class="hidden lg:visible lg:block"> &#9590; {{ money(totalPrice) }}</span>
      </ThemedButton>
    </div>
  </div>
  <div v-else>
    <VariantSizeSelector
      v-if="canDisplayVariantSelector"
      class="lg:mt-6"
      v-model="selections"
      :isGiftCertificate
      :masterVariantId="product.masterVariantId"
      :variantGroups="product.variantGroups"
      :variants="product.variants"
    />

    <template v-if="!selectedVariant.backordered">
      <BaseBodyText
        v-if="selectedVariant.maximumPiecesPerOrder < MAX_QUANTITY"
        class="px-4 py-2 mt-2 bg-amber-200 rounded-lg sm:mt-3"
      >
        Due to increased demand, this product is limited to
        {{ selectedVariant.maximumPiecesPerOrder }} per customer.
      </BaseBodyText>
      <DeliveryOptions
        v-if="!nextDelivery && isEligibleForAutoDelivery"
        v-model="selections.autoDelivery"
        class="mt-4"
        :variant="selectedVariant"
        @auto-delivery-learn-more="openAutoDeliveryModal('learn-more')"
      />
      <AddOnSection
        v-if="flags.showAddOn && product.offerGiftAddOns"
        class="mt-4 lg:mt-6"
        :selectedAddOns
        @open-modal="openAddOnModal"
      />
      <DeliveryEstimate
        v-if="!isGiftCertificate"
        class="mt-4 lg:mt-6"
        :hideTruckIcon="!!flags.showAddOn"
        :predictedShipping
        :quantity="selections.quantity"
        :selectedAddOns
        :sku="selectedVariant.sku"
        :totalPrice
        :weight="selectedVariant.weight"
      />
      <div class="h-11" :class="!isGiftCertificate ? 'mt-6' : 'mt-3 lg:mt-6'" ref="addToCartButton">
        <AddToAutoDelivery
          v-if="selections.autoDelivery && !nextDelivery"
          v-model="selections.quantity"
          :disabled="allBackordered"
          :isLoading="handleProductAdded.isPending"
          :maximumPiecesPerOrder="selectedVariant.maximumPiecesPerOrder ?? maxQuantity"
          :startAutoDelivery="!containsAutoDelivery"
          :totalCost="totalPrice"
          @submit="handleAddToAutoDelivery.execute"
        />
        <AddToCartQuantitySelect
          v-else
          v-model="selections.quantity"
          :disabled="allBackordered"
          :isLoading="handleProductAdded.isPending"
          :maximumPiecesPerOrder="selectedVariant.maximumPiecesPerOrder"
          :totalCost="totalPrice"
          @submit="handleProductAdded.execute"
        >
          Add <template v-if="selectedAddOns.length">All</template> to Cart &ndash;
          {{ money(totalPrice) }}
        </AddToCartQuantitySelect>
      </div>
      <transition name="slide-from-bottom" mode="out-in">
        <div
          v-show="isMounted && !isAddToCartVisible && !isFooterVisible"
          class="fixed right-0 bottom-0 left-0 z-10 p-2 mt-0 bg-white lg:hidden large-shadow"
        >
          <AddToAutoDelivery
            v-if="selections.autoDelivery && !nextDelivery"
            v-model="selections.quantity"
            data-test="add-to-auto-delivery-upsell-mobile"
            :disabled="allBackordered"
            :isLoading="handleProductAdded.isPending"
            :maximumPiecesPerOrder="selectedVariant.maximumPiecesPerOrder ?? maxQuantity"
            :startAutoDelivery="!containsAutoDelivery"
            :totalCost="totalPrice"
            @submit="handleAddToAutoDelivery.execute"
          />
          <AddToCartQuantitySelect
            v-else
            v-model="selections.quantity"
            data-test="add-to-cart-mobile"
            :disabled="allBackordered"
            :isLoading="handleProductAdded.isPending"
            :maximumPiecesPerOrder="selectedVariant.maximumPiecesPerOrder"
            :totalCost="totalPrice"
            @submit="handleProductAdded.execute"
          />
        </div>
      </transition>
      <AppliedDiscounts class="mt-2 sm:hidden" :discounts fullWidth :price="finalPrice" :savings />
      <UpcomingSubscriptionNotice
        v-if="activeSubscriptionsBySKU.has(selections.sku)"
        class="mt-4"
        :subscription="activeSubscriptionsBySKU.get(selections.sku)![0]"
      />
      <div
        v-else-if="nextDelivery && isEligibleForAutoDelivery"
        class="flex flex-col mt-4 bg-sky-50 rounded-lg"
      >
        <ThemedButton
          class="h-11 upsell-button"
          data-promo="1"
          data-promo-creative="PDP-Delivery Options"
          data-promo-name="Auto Delivery: Impulse Upsell"
          data-test="add-to-auto-delivery-upsell-button"
          fullWidth
          type="button"
          @click="openAutoDeliveryModal('sign-up')"
        >
          <span class="flex justify-center items-center w-full text-base leading-4">
            Save {{ autoDeliveryChannel.discount }}% Add to
            <img
              class="object-contain mt-0.5 ml-1 h-4"
              alt="auto delivery"
              src="@/assets/auto-delivery/logo-white.svg"
            />
          </span>
        </ThemedButton>
        <FreeShippingNotice :nextDelivery :price="totalPrice" />
      </div>
      <AutoDeliveryBenefits
        v-if="selections.autoDelivery && isEligibleForAutoDelivery && !nextDelivery"
        class="mt-4"
        @learn-more="openAutoDeliveryModal('learn-more')"
      />
      <UpsellingProducts
        v-if="!isGiftCertificate && product.upsellingProducts"
        class="mt-6"
        :skus="product.upsellingProducts.map((p) => p.masterData.current.masterVariant.sku ?? '')"
      />
      <FavoriteButton
        :autoDeliverySelections="selections.autoDelivery"
        :class="predictedShipping ? 'mt-2.5' : 'mt-6'"
        :isFavorited
        :product
        :selectedVariant
      />
      <GiftCheckbox v-if="!isGiftCertificate" v-model="selections.markedAsGift" class="mt-2.5" />
      <BulkDiscounts v-if="hasBulkDiscount && !finalPrice.discounted" class="mt-2.5" :product />
      <AddOnModal
        v-if="availableAddOns"
        v-model:selectedAddOns="selectedAddOns"
        :availableAddOns
        :isOpen="isAddOnModalOpen"
        :title="ADD_ON_MODAL_TITLE"
        @close-modal="isAddOnModalOpen = false"
      />
    </template>
    <AutoDeliveryModal
      v-model:interval="autoDeliverySignUpModal.interval"
      v-model:step="autoDeliverySignUpModal.step"
      :initialStep="autoDeliverySignUpModal.step"
      :isLoading="handleStartAutoDelivery.isPending || onAddToNextDelivery.isPending"
      :isOpen="autoDeliverySignUpModal.isOpen"
      :productDiscountNotice
      :showUpsellOption="!customer"
      :showNextDeliveries="!!nextDelivery"
      @add-to-existing-delivery="handleAutoDeliveryUpsell"
      @handle-close="closeAutoDeliveryModal"
      @start-auto-delivery="handleStartAutoDelivery.execute"
    />
  </div>
</template>

<style scoped lang="scss">
.large-shadow {
  box-shadow: 4px 4px 53px rgba(0, 0, 0, 0.25), 0px 4px 4px rgba(0, 0, 0, 0.25);

  @media screen and (min-width: 640px) {
    box-shadow: none;
  }
}

.opacity-enter-active,
.opacity-leave-active {
  @apply transition-opacity ease-in duration-300;
}
.opacity-enter-from,
.opacity-leave-to {
  @apply opacity-0;
}

.slide-from-bottom-enter-active,
.slide-from-bottom-leave-active {
  @apply transition ease-in-out duration-300;
}
.slide-from-bottom-enter-from,
.slide-from-bottom-leave-to {
  @apply translate-y-16 opacity-0;
}

.upsell-button {
  @apply bg-nuts-cyan-600 hover:bg-nuts-cyan-700;
}
</style>

<style lang="scss">
.page-pdp.offset-bottom-anchored-elements {
  // Attentive Widget
  iframe#attentive_creative,
  // DY Free shipping bubble
  #new-user-free-ship-banner {
    @apply mb-12 lg:mb-0;
  }
  #gladlyChat_container {
    bottom: 3rem !important;
    @apply lg:bottom-0;
  }
}

.page-pdp.offset-bottom-anchored-elements {
  // Gladly Chat FAB
  #gladlyChat_container {
    bottom: 3rem !important;
    @media screen and (min-width: 769px) {
      bottom: 0 !important;
    }
  }
}
</style>
